import { Filespace } from 'fs-api-client';
import { EventManager } from '../../services/EventManager';
import { registerCallback, unregisterCallback } from './DomainInjector';
import { isNewUser, NewUserLocalStoreFields, pushEventToAnalytics } from '../../utils/utilFunctions';
import StatusEnum = Filespace.StatusEnum;
import { WebportalEvent } from '../../services/WebportalEvent';

const fsStatusToEvent = {
    [StatusEnum.Provisioning]: WebportalEvent.FilespaceProvisioning,
    [StatusEnum.Provisioned]: WebportalEvent.FilespaceProvisioned,
    [StatusEnum.Initialized]: WebportalEvent.FilespaceInitialized,
    // [StatusEnum.Error]: EventName.FilespaceError,
};

const firstFsStatusToEvent = {
    [StatusEnum.Provisioning]: WebportalEvent.FirstFilespaceCreated,
    [StatusEnum.Provisioned]: WebportalEvent.FirstFilespaceProvisioned,
    [StatusEnum.Initialized]: WebportalEvent.FirstFilespaceInitialized,
    // [StatusEnum.Error]: EventName.FilespaceError,
};

abstract class IFilespaceEventsHandler {
    abstract onFilespaceDeleted(filespace: Filespace): void;
    abstract onFilespaceCreated(filespace: Filespace): void;
    abstract onFilespaceStatusChange(filespace: Filespace): void;
    abstract onDashboardEnter(): void;
    abstract onFilespaceWizardEnter(): void;
    abstract setUserId(userId: string | null): void;
}

class UserEventsHandler extends IFilespaceEventsHandler {
    onFilespaceDeleted(filespace: Filespace) {
        EventManager.fireCustomEvent(WebportalEvent.FilespaceDeleted, filespace);
    }

    onFilespaceCreated(filespace: Filespace) {
        EventManager.fireCustomEvent(WebportalEvent.FilespaceCreated, filespace);
    }

    onFilespaceStatusChange(filespace: Filespace) {
        const status = filespace.status!;
        const eventToFire = fsStatusToEvent[status];
        if (!eventToFire) { return; }
        EventManager.fireCustomEvent(eventToFire, filespace);
    }

    onDashboardEnter() {
        EventManager.fireCustomEvent(WebportalEvent.DashboardEnter);
    }

    onFilespaceWizardEnter() {
        EventManager.fireCustomEvent(WebportalEvent.FilespaceWizardEnter);
    }

    setUserId(userId: string | null) {}

}

class NewUserEventsHandler extends IFilespaceEventsHandler {
    constructor(protected currentUserId: string | null) {
        super();
    }

    onFilespaceCreated(filespace: Filespace) {
        if (this.getLocalStorageItem(NewUserLocalStoreFields.FIRST_WIZARD_OPEN)) {
            this.setLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_ID, filespace.id!);
            this.setLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_STATUS, filespace.status!.toString());
            this.removeLocalStorageItem(NewUserLocalStoreFields.FIRST_WIZARD_OPEN);
            EventManager.fireCustomEvent(WebportalEvent.FirstFilespaceCreated, filespace);
            return;
        }
        EventManager.fireCustomEvent(WebportalEvent.FilespaceCreated, filespace);
    }

    onFilespaceStatusChange(filespace: Filespace) {
        const status = filespace.status!;
        const firstFsId = this.getLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_ID);
        const isFirstFilespace = firstFsId && firstFsId === filespace.id!;
        const eventToFire = isFirstFilespace ? firstFsStatusToEvent[status] : fsStatusToEvent[status];
        if (!eventToFire) { return; }
        if (filespace.status === Filespace.StatusEnum.Initialized) {
            this.removeLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_ID);
            this.removeLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_STATUS);
        } else if (isFirstFilespace) {
            this.setLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_STATUS, filespace.status!.toString());
        }
        EventManager.fireCustomEvent(eventToFire, filespace);
    }

    onFilespaceDeleted(filespace: Filespace) {
        const firstFsId = this.getLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_ID);
        const isFirstFilespace = firstFsId && firstFsId === filespace.id!;
        if (isFirstFilespace) {
            this.removeLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_ID);
            this.removeLocalStorageItem(NewUserLocalStoreFields.FIRST_FS_STATUS);
        }
        EventManager.fireCustomEvent(WebportalEvent.FilespaceDeleted, filespace);
    }

    onDashboardEnter() {
        if (this.getLocalStorageItem(NewUserLocalStoreFields.FIRST_LOGIN)) {
            EventManager.fireCustomEvent(WebportalEvent.FirstTimeDashboardEnter);
            return;
        }
        EventManager.fireCustomEvent(WebportalEvent.DashboardEnter);
    }

    onFilespaceWizardEnter() {
        EventManager.fireCustomEvent(WebportalEvent.FilespaceWizardEnter);
        if (this.getLocalStorageItem(NewUserLocalStoreFields.FIRST_LOGIN)) {
            this.setLocalStorageItem(NewUserLocalStoreFields.FIRST_WIZARD_OPEN, '1');
            this.removeLocalStorageItem(NewUserLocalStoreFields.FIRST_LOGIN);
        }
    }

    setUserId(userId: string) { this.currentUserId = userId; }

    private getUserKey(key: string) {
        return `${this.currentUserId}_${key}`;
    }

    private getLocalStorageItem(key: string) {
        return localStorage.getItem(this.getUserKey(key));
    }

    private removeLocalStorageItem(key: string) {
        return localStorage.removeItem(this.getUserKey(key));
    }

    private setLocalStorageItem(key: string, value: string) {
        return localStorage.setItem(this.getUserKey(key), value);
    }

}

class EventsHandler {
    protected oldFilespaces: Filespace[] | null = null;
    protected isRegistered: boolean;
    protected filespaceEventsHandler: IFilespaceEventsHandler;
    protected currentUserId: string | null;

    constructor() {
        if (isNewUser()) {
            this.filespaceEventsHandler = new NewUserEventsHandler(this.currentUserId);
        } else {
            this.filespaceEventsHandler = new UserEventsHandler();
        }
        this.onNewFilespacesReceived = this.onNewFilespacesReceived.bind(this);
    }

    init(userId: string | null) {
        if (this.isRegistered) { return; }
        this.currentUserId = userId;
        this.filespaceEventsHandler.setUserId(userId);
        registerCallback(this.onNewFilespacesReceived);
        this.isRegistered = true;
    }

    deinit() {
        if (this.isRegistered) {
            unregisterCallback(this.onNewFilespacesReceived);
            this.isRegistered = false;
        }
    }

    onNewFilespacesReceived(newFilespaces: Filespace[]) {
        if (this.oldFilespaces === null) {
            this.oldFilespaces = newFilespaces;
            for (const filespace of newFilespaces) {
                EventManager.fireCustomEvent(WebportalEvent.FilespaceLoaded, filespace);
            }
            return;
        }
        for (const newFilespace of newFilespaces) {
            const oldFilespace = this.oldFilespaces.find((filespace: Filespace) => filespace.id === newFilespace.id);
            if (!oldFilespace) {
                this.filespaceEventsHandler.onFilespaceCreated(newFilespace);
                continue;
            }
            if (newFilespace.status !== oldFilespace.status) {
                this.filespaceEventsHandler.onFilespaceStatusChange(newFilespace);
            }
        }
        this.checkDeletedFilespaces(newFilespaces);
        this.oldFilespaces = newFilespaces;
    }

    handleFilespaceDeleted(filespace: Filespace) {
        EventManager.fireCustomEvent(WebportalEvent.FilespaceDeleted, filespace);
    }

    handleFilespaceCreated(filespace: Filespace) {
        EventManager.fireCustomEvent(WebportalEvent.FilespaceCreated, filespace);
    }

    handleFilespaceStatusChange(filespace: Filespace) {
        const status = filespace.status!;
        const eventToFire = fsStatusToEvent[status];
        if (!eventToFire) { return; }
        EventManager.fireCustomEvent(eventToFire, filespace);
    }

    onDashboardEnter() {
        this.filespaceEventsHandler.onDashboardEnter();
    }

    checkDeletedFilespaces(newFilespaces: Filespace[]) {
        for (const oldFilespace of this.oldFilespaces!) {
            const newFilespace = newFilespaces.find((filespace: Filespace) => filespace.id === oldFilespace.id);
            if (!newFilespace) {
                this.filespaceEventsHandler.onFilespaceDeleted(oldFilespace);
            }
        }
    }

    onDomainCreated() {
        localStorage.setItem(`${this.currentUserId}_${NewUserLocalStoreFields.FIRST_LOGIN}`, '1');
        pushEventToAnalytics('domain_added');
        this.filespaceEventsHandler = new NewUserEventsHandler(this.currentUserId);
        EventManager.fireCustomEvent(WebportalEvent.DomainCreated);
    }

    onFilespaceWizardEnter() {
        this.filespaceEventsHandler.onFilespaceWizardEnter();
    }

    onDashboardExit() {
        EventManager.fireCustomEvent(WebportalEvent.DashboardExit);
    }

    onFsInitializationWindowOpen(filespace: Filespace) {
        EventManager.fireCustomEvent(WebportalEvent.TryInitializeFilespace, filespace);
    }

    reset() {
        this.oldFilespaces = null;
        this.isRegistered = false;
    }
}

const eventHandler = new EventsHandler();
export { eventHandler as EventHandler };



// WEBPACK FOOTER //
// ./src/webportal/components/hocs/EventHandler.ts